// React and utilities
import { useCallback, useContext, useEffect, useMemo } from "react";

// Use page navigation after Customizr saves an image.
import { useNavigate } from "react-router-dom";

// Import Global context
// -------------------------------------

// Auth context provides user authentication status and token.
import { AuthContext } from "global/context/AuthContext";

// Use the new shared Customizr hook.
import { useCustomizr } from "global/hooks/useCustomizr";

// Import Global hooks
// -------------------------------------

// Hook for setting the page title.
import usePageTitle from "global/hooks/usePageTitle";

// Hook for showing toast notifications.
import { useToast } from "global/hooks/useToast";

// Import components
// -------------------------------------

// Workflow list panel / picker
import WorkflowListPanel from "./components/WorkflowListPanel";

// Main workflow form for configuring and starting workflows
import AiWorkflowForm from "./components/AiWorkflowForm";

// One Row In-progress queue section with progress bars, progress cards, collapse/expand, etc.
import InProgressQueueSection from "./components/InProgressQueueSection";

// Day slider for selecting which day's completed queue items to show.
import QueueDaySlider from "./components/QueueDaySlider";

// Completed queue section showing output images and details for the selected day.
import CompletedQueueSection from "./components/CompletedQueueSection";

// Queue item details popup with workflow info, prompts, outputs, and actions.
import QueueItemPopup from "./components/QueueItemPopup";

// Import Hooks
// -------------------------------------

// Workflow list management and loading.
import useWorkflowList from "./hooks/useWorkflowList";

// Handles workflow form state, input values, and run actions.
import useAiWorkflowManager from "./hooks/useAiWorkflowManager";

// Loads queue data and provides queue helper functions.
import useAiQueueManager from "./hooks/useAiQueueManager";

// Builds data for the in-progress queue section UI.
import useInProgressQueue from "./hooks/useInProgressQueue";

// Groups completed queue items by day for the day slider/history list.
import useQueueDayGroups from "./hooks/useQueueDayGroups";

// Controls which queue item is open in the popup and left/right navigation.
import useQueueItemPopup from "./hooks/useQueueItemPopup";

// Handles popup button actions like reuse, save, and open in Customizr.
import useQueueItemPopupActions from "./hooks/useQueueItemPopupActions";

// Import CSS
// -------------------------------------
import "./ai_image_generation.css";

// AI Studio page.
// This file mostly connects hooks and UI components.
// -------------------------------------
const AiImageGeneration = () => {

  // Set the page title.
  usePageTitle("AI Studio");

  // Use this to send the user to the image bucket after editing.
  const navigate = useNavigate();

  // Get auth token and auth ready status from AuthContext.
  const { token, authReady } = useContext(AuthContext);

  // Get the open function and the ready flag for Customizr.
  const { openCustomizr, isReady: isCustomizrReady } = useCustomizr();

  // Get toast methods for showing notifications.
  const { toastSuccess, toastError, toastInfo } = useToast();

  // Load available workflows.
  const { workflows, fetchWorkflows } = useWorkflowList(token);

  // Queue data and queue helper methods.
  const {

    // Full queue items and filtered queue lists used across sections.
    images,
    visibleImages,
    nonInProgressVisibleImages,

    // Queue identity and state helpers.
    getQueueItemIdentity,
    isInProgressQueueItem,
    isQueueItemQueued,
    getQueueItemProgressValue,
    getQueueItemProgressColor,

    // Output helpers for non-image queue results.
    getOutputEntries,
    getQueueItemOutputDescription,

    // Queue actions used by page components.
    handleDeleteImage,
    refreshQueueAfterWorkflowStart,
  } = useAiQueueManager({

    token,
    authReady,
    toastSuccess,
    toastError,

  });

  // Load workflows after auth is ready.
  useEffect(() => {

    if (!authReady) return;

    fetchWorkflows();

  }, [authReady, fetchWorkflows]);

  // Workflow form state and actions.
  const {

    // Current workflow and form values shown in the main form.
    selectedWorkflow,
    workflowFormValues,

    // Upload UI state for workflow image fields.
    workflowUploadingFields,

    // Prepared workflow field metadata for rendering.
    selectedWorkflowImageField,
    selectedWorkflowFormFields,
    isAnyWorkflowUploadInProgress,
    imageInputWorkflows,

    // Field type helpers and reusable value resolvers.
    isWorkflowImageField,
    isWorkflowPromptField,
    getPrimaryWorkflowPromptField,
    getWorkflowImageInputSettingKey,
    getReusableTextPrompt,
    getReusableImagePrompt,
    resolveWorkflowForFocusImage,
    formatFocusSettingValue,
    isEmptyFocusSettingValue,

    // Workflow selection/form lifecycle actions.
    getAIWorkFlow,
    getAIWorkflowForm,
    handleWorkflowFieldChange,
    clearSelectedWorkflowForm,

    // Image input actions.
    handleWorkflowImageFileSelected,

    // Run workflow and keep reusable prompt/image values.
    handleGenerateWorkflow,
    setLastMainTextPrompt,
    setLastMainImageValue,
  } = useAiWorkflowManager({

    token,
    workflows,
    toastSuccess,
    toastError,
    toastInfo,
    onWorkflowStarted: refreshQueueAfterWorkflowStart,

  });

  // Data used by the in-progress section.
  const {

    // Layout refs and collapse state for in-progress UI.
    inProgressRowRef,
    inProgressJobBarsRef,
    isInProgressCollapsed,
    setIsInProgressCollapsed,

    // In-progress item lists and counts for cards/bars.
    inProgressQueueItems,
    inProgressDisplayItems,
    hiddenInProgressCount,
    inProgressJobBarsLayout,
    totalInProgressCount,
  } = useInProgressQueue({

    visibleImages,
    isInProgressQueueItem,
    isQueueItemQueued,

  });

  // Data used by the completed section and day grouping.
  const {

    // Day-grouped completed queue items and selected day state.
    queueDayGroups,
    selectedQueueDayKey,
    setSelectedQueueDayKey,
    selectedQueueDayGroup,
  } = useQueueDayGroups(nonInProgressVisibleImages);


  // Popup focus state and left/right image navigation.
  const {

    // Current popup item and setter.
    focusImage,
    setFocusImage,

    // Open and navigate popup items.
    openFocusImage,
    showPrevImage,
    showNextImage,
    canNavigateFocusImage,
  } = useQueueItemPopup({

    token,
    images,
    getQueueItemIdentity,

  });

  // Actions available from the popup.
  const {

    // Helpers for deciding which reuse actions to show.
    isLikelyImageSource,
    hasReusableTextPrompt,

    // Reuse actions from popup back into workflow form.
    handleUseOutputDescription,
    handleUsePrompt,
    handleUseImage,

    // Save/edit/re-run actions from popup.
    handleSaveImageToBucket,
    handleOpenCustomizrClick,
    handleReuseFocusImageSettings,
    handleRunFocusImageWorkflow,

    // Save status flags for popup buttons/status UI.
    showSaveStatusForCurrentFocus,
    isSavingCurrentFocusImage,
    isSavingImageToBucket,
    saveImageToBucketStatus,
  } = useQueueItemPopupActions({

    focusImage,
    setFocusImage,
    selectedWorkflow,
    getPrimaryWorkflowPromptField,
    getReusableTextPrompt,
    getQueueItemOutputDescription,
    setLastMainTextPrompt,
    setLastMainImageValue,
    handleWorkflowFieldChange,
    isWorkflowImageField,
    resolveWorkflowForFocusImage,
    getAIWorkFlow,
    imageInputWorkflows,
    getWorkflowImageInputSettingKey,
    getQueueItemIdentity,
    token,
    openCustomizr,
    // Pass navigation down so the popup action can move to the image bucket.
    navigate,
    toastSuccess,
    toastError,
    toastInfo,

  });



  // Build a readable workflow title for a queue item.
  // Try exact workflow id first, then workflow code, then resolver fallback.
  const getWorkflowTitleForQueueItem = useCallback((img) => {

    // No image means no title to show.
    if (!img) return "";

    // First lookup: match queue item's workflow_id against workflows list.
    const workflowById = (workflows || []).find((workflow) => {

      // Skip id matching when queue item has no workflow_id.
      if (img.workflow_id === null || img.workflow_id === undefined) return false;

      // Compare as strings so "12" and 12 are treated as the same id.
      const imageWorkflowId = String(img.workflow_id);

      return String(workflow?.id) === imageWorkflowId;

    });

    // If id match has a name, use it as the title.
    if (workflowById?.name) return workflowById.name;

    // Second lookup: match queue item's workflow_code.
    const workflowByCode = (workflows || []).find((workflow) => workflow?.code === img.workflow_code);

    // If code match has a name, use that.
    if (workflowByCode?.name) return workflowByCode.name;

    // Third lookup: use shared resolver that infers workflow from queue item data.
    const resolvedWorkflow = resolveWorkflowForFocusImage(img);

    // If resolver finds a named workflow, use it.
    if (resolvedWorkflow?.name) return resolvedWorkflow.name;

    // Last fallback: show raw workflow code, or workflow id if code is missing.
    return img.workflow_code || String(img.workflow_id || "");

  }, [resolveWorkflowForFocusImage, workflows]);



  // Show prompt text first. If not present, show workflow title.
  const getQueueItemPromptDisplay = useCallback((img) => {

    const prompt = getReusableTextPrompt(img?.settings);

    if (prompt) return prompt;

    return getWorkflowTitleForQueueItem(img);

  }, [getReusableTextPrompt, getWorkflowTitleForQueueItem]);



  // Delete item from queue. Close popup if that item is open.
  const handleDeleteQueueItem = useCallback((jobId) => {

    handleDeleteImage(jobId, () => {

      // Close popup if deleted item was focused.
      setFocusImage(null);

    });

  }, [handleDeleteImage, setFocusImage]);



  // Open a new POD design directly with the selected AI image
  const handleCreatePodProduct = useCallback((image) => {

    // Send the initial image and title through React Router state
    navigate("/designs", {

      state: {

        createPodDesign: {

          // Use the selected AI output as the first design image
          image_url: image?.image_url,

          // Use the AI job title as the new POD design title
          title: image?.title || "Untitled",

        },

      },

    });

  }, [navigate]);

  // Values passed into the popup UI.
  // Resolve which workflow this focused queue item most likely came from.
  // Prefer the complete workflow returned with the detailed queue item.
  let focusImageWorkflow = null;

  if (focusImage?.workflow) focusImageWorkflow = focusImage.workflow;

  // Resolve from the workflow list when the detailed queue item does not contain workflow data.
  if (!focusImageWorkflow && focusImage) focusImageWorkflow = resolveWorkflowForFocusImage(focusImage);

  // True when focused item is still in progress (not completed yet).
  const focusImageIsInProgress = Boolean(focusImage && isInProgressQueueItem(focusImage));

  // True when focused in-progress item is still waiting in queue.
  const focusImageIsQueued = Boolean(focusImage && isQueueItemQueued(focusImage));

  // Progress/countdown value used by popup radial progress UI.
  const focusImageProgressValue = focusImage ? getQueueItemProgressValue(focusImage) : 0;

  // Progress ring color (queued vs running state).
  const focusImageProgressColor = focusImage ? getQueueItemProgressColor(focusImage) : "#007bff";

  // Best available image input value from focused item settings.
  const focusImageImagePrompt = getReusableImagePrompt(focusImage?.settings, focusImageWorkflow);

  // Best available text prompt for focused item (settings first, fallback display text).
  const focusImageTextPrompt = getReusableTextPrompt(focusImage?.settings) || getQueueItemPromptDisplay(focusImage);

  // Output entries for output-only queue items shown in popup details.
  const focusImageOutputEntries = useMemo(() => getOutputEntries(focusImage?.output), [focusImage, getOutputEntries]);

  // Description value extracted from queue output (if present).
  const focusImageOutputDescription = focusImage ? getQueueItemOutputDescription(focusImage) : "";

  // Only show user-editable workflow settings in popup details.
  const focusImageEditableSettings = useMemo(() => {

    if (!focusImageWorkflow) return [];

    return (focusImageWorkflow.settings || []).filter(
      (field) => field?.user_editable === "Y" || field?.user_editable === true
    );

  }, [focusImageWorkflow]);

  return (
    <>
      <div className="ai-page-layout">

        <div className="ai-top-row">

          {/* Left side workflow list */}
          <WorkflowListPanel
            workflows={workflows}
            selectedWorkflowCode={selectedWorkflow?.code || ""}
            onSelectWorkflow={getAIWorkflowForm}
          />

          {/* Main workflow form */}
          <AiWorkflowForm
            selectedWorkflow={selectedWorkflow}
            selectedWorkflowImageField={selectedWorkflowImageField}
            selectedWorkflowFormFields={selectedWorkflowFormFields}
            workflowFormValues={workflowFormValues}
            workflowUploadingFields={workflowUploadingFields}
            isWorkflowImageField={isWorkflowImageField}
            isWorkflowPromptField={isWorkflowPromptField}
            handleWorkflowImageFileSelected={handleWorkflowImageFileSelected}
            handleWorkflowFieldChange={handleWorkflowFieldChange}
            clearSelectedWorkflowForm={clearSelectedWorkflowForm}
            handleGenerateWorkflow={handleGenerateWorkflow}
            authReady={authReady}
            isAnyWorkflowUploadInProgress={isAnyWorkflowUploadInProgress}
          />
        </div>

        <div className="ai-queue-pane">

          {/* In-progress queue area */}
          <InProgressQueueSection
            isInProgressCollapsed={isInProgressCollapsed}
            setIsInProgressCollapsed={setIsInProgressCollapsed}
            totalInProgressCount={totalInProgressCount}
            inProgressJobBarsLayout={inProgressJobBarsLayout}
            inProgressJobBarsRef={inProgressJobBarsRef}
            hiddenInProgressCount={hiddenInProgressCount}
            inProgressRowRef={inProgressRowRef}
            inProgressQueueItems={inProgressQueueItems}
            inProgressDisplayItems={inProgressDisplayItems}
            getQueueItemIdentity={getQueueItemIdentity}
            getQueueItemProgressValue={getQueueItemProgressValue}
            getQueueItemProgressColor={getQueueItemProgressColor}
            getWorkflowTitleForQueueItem={getWorkflowTitleForQueueItem}
            getQueueItemPromptDisplay={getQueueItemPromptDisplay}
            onOpenFocusImage={openFocusImage}
            onDeleteImage={handleDeleteQueueItem}
          />

          {/* Day slider for queue history */}
          <QueueDaySlider
            queueDayGroups={queueDayGroups}
            selectedQueueDayKey={selectedQueueDayKey}
            onSelectedQueueDayKeyChange={setSelectedQueueDayKey}
            isPopupOpen={Boolean(focusImage)}
          />

          {/* Completed items for the selected day */}
          <CompletedQueueSection
            selectedQueueDayGroup={selectedQueueDayGroup}
            getQueueItemIdentity={getQueueItemIdentity}
            getOutputEntries={getOutputEntries}
            getQueueItemPromptDisplay={getQueueItemPromptDisplay}
            onOpenFocusImage={openFocusImage}
            onDeleteImage={handleDeleteQueueItem}
          />

        </div>
        
      </div>

      {/* Popup for queue item details and actions */}
      <QueueItemPopup
        focusImage={focusImage}
        onClose={() => setFocusImage(null)}
        canNavigateFocusImage={canNavigateFocusImage}
        onShowPrevImage={showPrevImage}
        onShowNextImage={showNextImage}
        focusImageIsInProgress={focusImageIsInProgress}
        focusImageIsQueued={focusImageIsQueued}
        focusImageImagePrompt={focusImageImagePrompt}
        focusImageTextPrompt={focusImageTextPrompt}
        focusImageOutputEntries={focusImageOutputEntries}
        focusImageOutputDescription={focusImageOutputDescription}
        isLikelyImageSource={isLikelyImageSource}
        focusImageProgressValue={focusImageProgressValue}
        focusImageProgressColor={focusImageProgressColor}
        focusImageWorkflow={focusImageWorkflow}
        focusImageEditableSettings={focusImageEditableSettings}
        isEmptyFocusSettingValue={isEmptyFocusSettingValue}
        isWorkflowImageField={isWorkflowImageField}
        isWorkflowPromptField={isWorkflowPromptField}
        formatFocusSettingValue={formatFocusSettingValue}
        showSaveStatusForCurrentFocus={showSaveStatusForCurrentFocus}
        saveImageToBucketStatus={saveImageToBucketStatus}
        onCreatePodProduct={handleCreatePodProduct}
        hasReusableTextPrompt={hasReusableTextPrompt}
        handleUseOutputDescription={handleUseOutputDescription}
        handleUsePrompt={handleUsePrompt}
        handleUseImage={handleUseImage}
        handleReuseFocusImageSettings={handleReuseFocusImageSettings}
        imageInputWorkflows={imageInputWorkflows}
        handleRunFocusImageWorkflow={handleRunFocusImageWorkflow}
        handleOpenCustomizrClick={handleOpenCustomizrClick}
        isCustomizrReady={
          // Tell the popup when Customizr is ready to use.
          isCustomizrReady
        }
        isSavingCurrentFocusImage={isSavingCurrentFocusImage}
        handleSaveImageToBucket={handleSaveImageToBucket}
        isSavingImageToBucket={isSavingImageToBucket}
        handleDeleteImage={handleDeleteQueueItem}
      />
    </>
  );

};

export default AiImageGeneration;
